import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  inject,
  model,
  TemplateRef,
  effect,
  untracked,
  contentChildren,
  afterNextRender,
  Injector,
  DestroyRef,
  signal,
  OnDestroy,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { delay, filter, of } from 'rxjs';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { PermissionsService } from '../../../core/common/services/permissions/permissions.service';
import { PermissionsDirective } from '../../directives/permissions/permissions.directive';
import { TabComponent } from './tab/tab.component';

@Component({
  selector: 'lib-tabs',
  imports: [PermissionsDirective, NgTemplateOutlet],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TabsComponent implements OnDestroy {
  private readonly featureFlags = inject(FeatureFlagsService);
  protected readonly systemDefaultColor = computed(() =>
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_default_color
    )
  );
  permissionsService = inject(PermissionsService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  injector = inject(Injector);
  destroyRef = inject(DestroyRef);
  tabs = contentChildren(TabComponent, { descendants: true });
  tabPill = input<boolean>(false);
  selectTab = output<number>();
  tabsBorder = input<boolean>(false);
  tabsClass = input<string>('');
  preventSwitchTabs = input<boolean>(false);
  discard = output<void>();
  activeTabIndex = model<number>(0);
  tabActionSectionRef = input<TemplateRef<unknown> | null>(null); // new implementation for tab action section
  tabSegmented = input<boolean>(false);
  tabsStartRef = input<TemplateRef<unknown> | null>(null);
  // Extra classes for the tabs row and the content area (both variants), and the segmented control
  tabsHeaderClass = input<string>('');
  segmentedClass = input<string>('bg-neutral-150');
  tabsContentClass = input<string>('');
  enableTabRouting = input<boolean>(false);
  paramKey = input<string>('tab');
  initialLoad = signal<boolean>(true);
  // tracks the parent route path (including route params) where tabs are rendered;
  // Angular reuses this component when only route params change (e.g. switching
  // between two entities on the same route) without destroying/recreating it
  private currentParentRoutePath: string | null = null;

  tabsEffect = effect(() => {
    const tabs = this.tabs();

    if (tabs?.length) {
      this.initializeActiveTab();
      this.tabsEffect.destroy();
    }
  });

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const parentRoutePath = this.getParentRoutePath();
        if (
          this.tabs()?.length &&
          this.currentParentRoutePath !== null &&
          parentRoutePath !== this.currentParentRoutePath
        ) {
          this.initializeActiveTab();
        }
      });
  }

  ngOnDestroy(): void {
    const parentRoutePath = this.currentParentRoutePath;
    if (parentRoutePath && this.enableTabRouting()) {
      const currentUrl = this.getCurrentPath();
      if (!this.isChildRouteOfParent(currentUrl, parentRoutePath)) {
        this.clearStoredTab(parentRoutePath);
      }
    }
  }

  private getCurrentPath(): string {
    return this.router.url.split('?')[0];
  }

  private getParentRoutePath(): string {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.activatedRoute,
    });
    return this.router.serializeUrl(urlTree).split('?')[0];
  }

  private isChildRouteOfParent(
    currentUrl: string,
    parentRoutePath: string
  ): boolean {
    return currentUrl.startsWith(`${parentRoutePath}/`);
  }

  private initializeActiveTab(): void {
    this.currentParentRoutePath = this.getParentRoutePath();
    if (this.enableTabRouting()) {
      // To ensure child inputs are propagated
      afterNextRender(
        () => {
          this.handleTabRouting();
        },
        { injector: this.injector }
      );
    } else {
      this.handleFirstVisibleTab();
    }
  }

  activeTabIndexEffect = effect(() => {
    const activeIndex = this.activeTabIndex();
    const tabs = untracked(() => this.tabs());
    const initialLoad = untracked(() => this.initialLoad());
    if (!initialLoad && tabs?.length && activeIndex !== null) {
      const activeTab = this.handleInitialSelectedTab()[activeIndex];
      this.onSelectTab(activeTab, activeIndex);
    }
  });

  handleTabRouting() {
    const tabParam =
      this.activatedRoute.snapshot.queryParams[this.paramKey()] ??
      this.getStoredTab();
    const tabComponent = this.handleInitialSelectedTab().find(
      tab => String(tab.tabRoutingParam()) === String(tabParam)
    );

    if (tabComponent) {
      const originalTabIndex = this.tabs().indexOf(tabComponent);
      this.onSelectTab(tabComponent, originalTabIndex);
    } else {
      this.handleFirstVisibleTab();
    }
  }

  private getStorageKey(parentRoutePath?: string): string {
    return `lib-tabs:${parentRoutePath ?? this.currentParentRoutePath ?? this.getParentRoutePath()}:${this.paramKey()}`;
  }

  private getStoredTab(): string | null {
    return sessionStorage.getItem(this.getStorageKey());
  }

  private storeTab(tabRoutingParam: string | number | undefined): void {
    if (tabRoutingParam === undefined) return;
    sessionStorage.setItem(this.getStorageKey(), String(tabRoutingParam));
  }

  private clearStoredTab(parentRoutePath: string): void {
    sessionStorage.removeItem(this.getStorageKey(parentRoutePath));
  }

  handleFirstVisibleTab() {
    const tab = this.handleInitialSelectedTab()[this.activeTabIndex() || 0];
    const originalTabIndex = this.tabs().indexOf(tab);
    this.onSelectTab(tab, originalTabIndex);
  }

  handleInitialSelectedTab(): TabComponent[] {
    if (this.tabs().length) {
      return this.tabs().filter(tab => {
        return this.permissionsService.checkPermission(
          tab.featurePermissions(),
          tab.userPermissions()
        );
      });
    } else {
      return [];
    }
  }

  onTabClick(tab: TabComponent, index: number) {
    if (tab.disable() || tab.active()) {
      return;
    }

    this.onSelectTab(tab, index);
    this.selectTab.emit(index);
  }

  onSelectTab(tab: TabComponent, index: number) {
    if (!tab || tab.active()) {
      return;
    }

    if (!this.preventSwitchTabs()) {
      this.tabs().forEach(tab => tab.active.set(false));
      tab.active.set(true);
      if (this.initialLoad()) {
        this.activeTabIndex.set(index);
      }

      this.handleTabRoutingParamAfterTabSelection(tab);
    }
  }

  handleTabRoutingParamAfterTabSelection(tab: TabComponent) {
    // handle tab routing
    if (this.enableTabRouting()) {
      this.storeTab(tab.tabRoutingParam());
      of(null)
        .pipe(delay(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          const queryParams = this.activatedRoute.snapshot.queryParams;
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
              ...queryParams,
              [this.paramKey()]: tab.tabRoutingParam(),
            },
          });
          this.initialLoad.set(false);
        });
    }
  }
}
